import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type ScheduleItem,
  type ScheduleLink,
  getStatusColor,
  getPhaseColor,
  formatDateLabel,
  formatDateFull,
  getBarColor,
} from '@/lib/scheduleUtils';
import type { ScheduleChecklistProgress } from '@/hooks/useScheduleChecklistProgress';
import type { LinkedChecklistItem } from '@/lib/scheduleChecklistProgress';
import { isScheduleItemFinalComplete } from '@/lib/scheduleChecklistProgress';
import LinkTypeBadge from './LinkTypeBadge';

interface Props {
  items: ScheduleItem[];
  links: ScheduleLink[];
  canEdit: boolean;
  criticalItemIds: Set<string>;
  checklistProgressMap: Map<string, ScheduleChecklistProgress>;
  linkedChecklists?: Map<string, LinkedChecklistItem[]>;
  onEdit: (item: ScheduleItem) => void;
  onToggleComplete: (item: ScheduleItem, checked: boolean) => void;
  onOpenCompletionBlock: (item: ScheduleItem) => void;
  onSendInstruction?: (item: ScheduleItem) => void;
  viewerRole?: 'management' | 'subcontractor' | 'client';
}

export default function ScheduleMobileListView({
  items,
  links,
  canEdit,
  criticalItemIds,
  checklistProgressMap,
  linkedChecklists,
  onEdit,
  onToggleComplete,
  onOpenCompletionBlock,
  onSendInstruction,
  viewerRole = 'management',
}: Props) {
  const { t } = useTranslation();
  const [expandedId, setExpandedId] = useState<string | null>(null);

  if (items.length === 0) {
    return (
      <div className="text-center py-12 px-4">
        <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 rounded-2xl bg-[#F9FAFB]">
          <i className="ri-calendar-todo-line text-[#D1D5DB] text-2xl"></i>
        </div>
        <p className="text-[#374151] font-semibold text-sm">
          {t('schedule.noItemsMatchCurrentFilter')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3 pb-4">
      {items.map((item, idx) => {
        const isExpanded = expandedId === item.id;
        const cp = checklistProgressMap.get(item.id);
        const hasLinked = cp?.hasLinkedItems ?? false;
        const checklistPct = cp?.progressPct ?? 0;
        const checklistBlocked = hasLinked && !cp?.allRequiredComplete;
        const displayPct = hasLinked ? checklistPct : item.progress;

        const isComplete = isScheduleItemFinalComplete(
          { is_complete: item.is_complete, progress: item.progress, actual_end_date: item.actual_end_date },
          hasLinked ? (linkedChecklists?.get(item.id) ?? []) : []
        );
        const isCritical = criticalItemIds.has(item.id);

        const itemLinks = links.filter(
          (l) => l.predecessor_item_id === item.id || l.successor_item_id === item.id
        );

        const phaseColor = getPhaseColor(item.phase);

        return (
          <div
            key={item.id}
            className="bg-white rounded-xl border border-gray-100 overflow-hidden"
          >
            {/* Card header */}
            <button
              onClick={() => setExpandedId(isExpanded ? null : item.id)}
              className="w-full text-left p-4 cursor-pointer"
            >
              {/* Top row: status + number + title */}
              <div className="flex items-start gap-3">
                {/* Completion status */}
                <div className="flex-shrink-0 pt-0.5">
                  {canEdit ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!isComplete && checklistBlocked && viewerRole !== 'management') {
                          onOpenCompletionBlock(item);
                          return;
                        }
                        onToggleComplete(item, !isComplete);
                      }}
                      className={`
                        flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold cursor-pointer whitespace-nowrap transition-all
                        ${isComplete
                          ? 'bg-[#F0FDF4] text-[#059669] border border-[#BBF7D0]'
                          : 'bg-[#FEF0E7] text-[#E8621A] border border-[#FDBA74]'
                        }
                      `}
                    >
                      {isComplete ? (
                        <i className="ri-checkbox-circle-line text-[11px]"></i>
                      ) : (
                        <i className="ri-checkbox-blank-circle-line text-[11px]"></i>
                      )}
                      {isComplete ? t('schedule.completed') : t('schedule.complete')}
                    </button>
                  ) : (
                    <span
                      className={`
                        flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold whitespace-nowrap
                        ${isComplete
                          ? 'bg-[#F0FDF4] text-[#059669] border border-[#BBF7D0]'
                          : 'bg-[#F9FAFB] text-[#9CA3AF] border border-gray-200'
                        }
                      `}
                    >
                      {isComplete ? (
                        <i className="ri-checkbox-circle-line text-[11px]"></i>
                      ) : (
                        <i className="ri-checkbox-blank-circle-line text-[11px]"></i>
                      )}
                      {isComplete ? t('schedule.completed') : t('schedule.pendingItems')}
                    </span>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-[#9CA3AF] text-xs font-mono">#{item.task_no ?? idx + 1}</span>
                    {isCritical && (
                      <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-[#EF4444]"></span>
                    )}
                    <span className="text-[15px] font-semibold text-[#1A1A1A] line-clamp-2 leading-snug">
                      {item.title}
                    </span>
                  </div>

                  {/* Phase + Stage badges */}
                  <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                    {item.phase && (
                      <span
                        className="text-[11px] font-bold px-1.5 py-0.5 rounded bg-gray-100"
                        style={{ color: phaseColor }}
                      >
                        {item.phase}
                      </span>
                    )}
                    {item.main_stage && (
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#F9FAFB] text-[#9CA3AF] flex items-center gap-1">
                        <i className="ri-folder-line text-[9px]"></i>
                        {item.main_stage}
                      </span>
                    )}
                    {item.construction_sub_stage && (
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#FEF0E7] text-[#E8621A]">
                        {item.construction_sub_stage}
                      </span>
                    )}
                  </div>
                </div>

                {/* Progress badge */}
                <div className="flex-shrink-0 flex flex-col items-end gap-1">
                  <span
                    className="text-xs font-bold px-2 py-0.5 rounded-full"
                    style={{
                      backgroundColor: getStatusColor(item.progress, item.is_complete).bg,
                      color: getStatusColor(item.progress, item.is_complete).text,
                    }}
                  >
                    {displayPct}%
                  </span>
                  {hasLinked && (
                    <span className="text-[10px] text-[#0891B2] bg-[#F0F9FF] px-1.5 py-0.5 rounded-full font-bold">
                      CL
                    </span>
                  )}
                </div>
              </div>

              {/* Main info row */}
              <div className="mt-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-[#6B7280]">
                    <i className="ri-time-line mr-1 text-[10px]"></i>
                    {item.duration_days} {item.duration_days === 1 ? t('schedule.daySingular') : t('schedule.dayPlural')}
                  </span>
                  <span className="text-xs text-[#374151] font-medium">
                    {formatDateLabel(item.start_date)} → {formatDateLabel(item.end_date)}
                  </span>
                </div>

                {/* Progress bar */}
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{
                      width: `${displayPct}%`,
                      backgroundColor: isComplete ? '#059669' : '#E8621A',
                    }}
                  />
                </div>

                {/* Checklist mapping status */}
                <div className="flex items-center justify-between">
                  {hasLinked ? (
                    <span className="text-[11px] text-[#6B7280]">
                      {cp?.allRequiredComplete ? (
                        <span className="text-[#059669] font-semibold">
                          <i className="ri-checkbox-circle-line mr-0.5"></i>
                          {cp.completedRequiredCount}/{cp.requiredCount} {t('checklist.finalApproval')}
                        </span>
                      ) : (
                        <span className="text-[#F59E0B] font-semibold">
                          <i className="ri-error-warning-line mr-0.5"></i>
                          {cp?.missingManagement ?? 0} mgmt · {cp?.missingSubcontractor ?? 0} sub {t('checklist.pending')}
                        </span>
                      )}
                    </span>
                  ) : (
                    <span className="text-[11px] text-[#9CA3AF] font-semibold bg-[#FFFBEB] px-1.5 py-0.5 rounded-full">
                      <i className="ri-link-unlink-m mr-0.5"></i>
                      {t('schedule.noChecklistMapped')}
                    </span>
                  )}
                  {itemLinks.length > 0 && (
                    <span className="text-[11px] text-[#6B7280] font-semibold bg-[#F3F4F6] px-1.5 py-0.5 rounded-full">
                      <i className="ri-links-line mr-0.5"></i>
                      {itemLinks.length} {itemLinks.length === 1 ? t('schedule.linkSingular') : t('schedule.linkPlural')}
                    </span>
                  )}
                </div>
              </div>
            </button>

            {/* Expanded details */}
            {isExpanded && (
              <div className="px-4 pb-4 border-t border-gray-100">
                <div className="pt-3 space-y-3">
                  {/* Dates grid */}
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-[#F9FAFB] rounded-lg p-2.5">
                      <p className="text-[#9CA3AF] text-[10px] font-semibold uppercase tracking-wider mb-1">
                        {t('scheduleModal.startDate')}
                      </p>
                      <p className="text-[#374151] text-xs font-medium">
                        {formatDateFull(item.start_date)}
                      </p>
                    </div>
                    <div className="bg-[#F9FAFB] rounded-lg p-2.5">
                      <p className="text-[#9CA3AF] text-[10px] font-semibold uppercase tracking-wider mb-1">
                        {t('scheduleModal.endDate')}
                      </p>
                      <p className="text-[#374151] text-xs font-medium">
                        {formatDateFull(item.end_date)}
                      </p>
                    </div>
                    <div className="bg-[#F9FAFB] rounded-lg p-2.5">
                      <p className="text-[#9CA3AF] text-[10px] font-semibold uppercase tracking-wider mb-1">
                        {t('scheduleModal.durationLabel')}
                      </p>
                      <p className="text-[#374151] text-xs font-medium">
                        {item.duration_days} {item.duration_days === 1 ? t('schedule.daySingular') : t('schedule.dayPlural')}
                      </p>
                    </div>
                    <div className="bg-[#F9FAFB] rounded-lg p-2.5">
                      <p className="text-[#9CA3AF] text-[10px] font-semibold uppercase tracking-wider mb-1">
                        {t('scheduleModal.assignedTo')}
                      </p>
                      <p className="text-[#374151] text-xs font-medium">
                        {item.assigned_role || item.assigned_to || '—'}
                      </p>
                    </div>
                  </div>

                  {/* Checklist detail */}
                  {cp && cp.hasLinkedItems && (
                    <div className={`rounded-lg p-3 border ${cp.allRequiredComplete ? 'bg-[#F0FDF4] border-[#BBF7D0]' : 'bg-[#FEF0E7] border-[#FDBA74]'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-semibold text-[#374151]">
                          {t('scheduleModal.checklistProgress')}
                        </span>
                        <span className={`text-xs font-bold ${cp.allRequiredComplete ? 'text-[#059669]' : 'text-[#92400E]'}`}>
                          {cp.progressPct}%
                        </span>
                      </div>
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all"
                          style={{
                            width: `${cp.progressPct}%`,
                            backgroundColor: cp.allRequiredComplete ? '#059669' : '#E8621A',
                          }}
                        />
                      </div>
                      <div className="flex items-center gap-3 mt-2 flex-wrap text-[11px]">
                        <span className="text-[#9CA3AF]">
                          Mgmt: <span className="font-semibold text-[#374151]">{cp.managementCheckedCount}</span>
                        </span>
                        <span className="text-[#9CA3AF]">
                          Sub: <span className="font-semibold text-[#374151]">{cp.subcontractorCheckedCount}</span>
                        </span>
                        {cp.missingManagement > 0 && (
                          <span className="text-[#F59E0B] font-semibold">
                            {cp.missingManagement} {t('checklist.mgmtPending')}
                          </span>
                        )}
                        {cp.missingSubcontractor > 0 && (
                          <span className="text-[#F59E0B] font-semibold">
                            {cp.missingSubcontractor} {t('checklist.subPending')}
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Links */}
                  {itemLinks.length > 0 && (
                    <div className="bg-[#F9FAFB] rounded-lg p-3">
                      <p className="text-[#9CA3AF] text-[10px] font-semibold uppercase tracking-wider mb-2">
                        {t('scheduleModal.linkedScheduleItems')}
                      </p>
                      <div className="space-y-1.5">
                        {itemLinks.map((l) => {
                          const isPre = l.predecessor_item_id === item.id;
                          const otherId = isPre ? l.successor_item_id : l.predecessor_item_id;
                          const other = items.find((i) => i.id === otherId);
                          return (
                            <div key={l.id} className="flex items-center gap-2 text-xs">
                              <LinkTypeBadge type={l.link_type} />
                              <span className="text-[#374151] font-medium truncate">
                                {isPre ? '→' : '←'} {other?.title || 'Unknown'}
                              </span>
                              {l.lag_days > 0 && (
                                <span className="text-[#9CA3AF] text-[10px]">+{l.lag_days}d</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Notes */}
                  {item.notes && (
                    <p className="text-[#6B7280] text-xs leading-relaxed bg-[#F9FAFB] rounded-lg p-3">
                      {item.notes}
                    </p>
                  )}

                  {/* Action buttons */}
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => onEdit(item)}
                      className="flex items-center justify-center gap-1.5 py-2.5 rounded-lg bg-white border border-gray-200 text-[#374151] text-xs font-semibold cursor-pointer whitespace-nowrap hover:bg-gray-50 transition-colors"
                    >
                      <i className="ri-pencil-line text-sm"></i>
                      {t('btn.edit')}
                    </button>
                    {canEdit && (
                      <button
                        onClick={() => onToggleComplete(item, !isComplete)}
                        disabled={!isComplete && checklistBlocked && viewerRole !== 'management'}
                        className={`flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-xs font-semibold cursor-pointer whitespace-nowrap transition-colors disabled:opacity-50 ${
                          isComplete
                            ? 'bg-[#FEF0E7] text-[#92400E] border border-[#FDBA74]'
                            : 'bg-[#059669] text-white hover:bg-[#047857]'
                        }`}
                      >
                        <i className={`${isComplete ? 'ri-checkbox-indeterminate-line' : 'ri-checkbox-circle-line'} text-sm`}></i>
                        {isComplete ? t('btn.markComplete') : t('btn.markComplete')}
                      </button>
                    )}
                    {onSendInstruction && (
                      <button
                        onClick={() => onSendInstruction(item)}
                        className="flex items-center justify-center gap-1.5 py-2.5 rounded-lg bg-white border border-gray-200 text-[#059669] text-xs font-semibold cursor-pointer whitespace-nowrap hover:bg-[#F0FDF4] transition-colors col-span-2"
                      >
                        <i className="ri-send-plane-line text-sm"></i>
                        {t('btn.sendInstruction')}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}